import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, FieldError, Input, Label, TextField } from '@heroui/react';

import AuthLayout, { AuthLink } from '../components/common/AuthLayout';
import Notice from '../components/common/Notice';
import PasswordField from '../components/common/PasswordField';
import useValidation from '../components/common/useValidation';
import userService from '../services/auth.service';
import { internalPathOf } from '../services/internalPath';
import * as check from '../services/validation';
import { messageOf } from '../i18n/el';

// Η οθόνη σύνδεσης. Η άφιξη εδώ τερματίζει όποια συνεδρία είχε απομείνει, ώστε
// άκυρο διακριτικό να μην επιβιώνει σε δεύτερη προσπάθεια.

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isPending, setIsPending] = useState(false);
    const [error, setError] = useState('');

    const form = useValidation();
    const navigate = useNavigate();
    const location = useLocation();

    // Η άφιξη στην οθόνη σύνδεσης τερματίζει όποια συνεδρία είχε απομείνει.
    useEffect(() => { userService.logout(); }, []);

    // Στη σύνδεση ο έλεγχος είναι μόνο «όχι κενό»: ο κανόνας των 8 χαρακτήρων
    // αφορά τη **δημιουργία** κωδικού. Ένας παλιός λογαριασμός με κοντότερο
    // κωδικό πρέπει να μπορεί να συνδεθεί.
    const emailMessage = form.show('email', check.required(email, 'Η διεύθυνση είναι υποχρεωτική.'));
    const passwordMessage = form.show('password', check.required(password, 'Ο κωδικός είναι υποχρεωτικός.'));

    function handleSubmit(event) {
        event.preventDefault();
        form.markSubmitted();
        setError('');

        if (!(email && password)) return;

        setIsPending(true);
        userService.login(email, password).then(
            () => {
                const { from } = location.state || { from: { pathname: '/' } };
                // Σε αποτυχία του ελέγχου η σύνδεση θεωρείται επιτυχής και ο χρήστης
                // πηγαίνει στην αρχική. Δεν εμφανίζεται μήνυμα: δεν έκανε αυτός το λάθος.
                navigate(internalPathOf(from));
            },
            // Λάθος διαπιστευτήρια: μήνυμα και **καμία αποσύνδεση**. Η συνεδρία δεν
            // υπήρξε ποτέ, οπότε δεν υπάρχει τι να λήξει. Η ειδοποίηση δεν κλείνει:
            // είναι ο λόγος για τον οποίο η φόρμα δεν προχώρησε (ΜΛΑ-03).
            //
            // Το μήνυμα του API είναι στα αγγλικά κατά σχεδιασμό και μεταφράζεται
            // εδώ από τον κωδικό της D4, όχι από το κείμενο.
            (apiError) => {
                setError(messageOf(apiError));
                setIsPending(false);
            }
        );
    }

    return (
        <AuthLayout
            title="Καλώς ορίσατε"
            subtitle="Συνδεθείτε για να δείτε τις θεματικές συλλογές σας"
            footer={<AuthLink to="/reset">Ξεχάσατε τον κωδικό σας;</AuthLink>}
        >
            {error && <Notice status="danger" title="Η σύνδεση απέτυχε">{error}</Notice>}

            <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
                <TextField
                    name="email"
                    type="email"
                    value={email}
                    onChange={setEmail}
                    onBlur={form.touch('email')}
                    isInvalid={Boolean(emailMessage)}
                    autoComplete="email"
                >
                    <Label>Διεύθυνση ηλεκτρονικού ταχυδρομείου</Label>
                    <Input placeholder="name@example.gr" />
                    <FieldError>{emailMessage}</FieldError>
                </TextField>

                <PasswordField
                    value={password}
                    onChange={setPassword}
                    onBlur={form.touch('password')}
                    isInvalid={Boolean(passwordMessage)}
                    errorMessage={passwordMessage}
                />

                <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                    <Button type="submit" isPending={isPending} className="flex-1">
                        Σύνδεση
                    </Button>
                    <Button variant="outline" onPress={() => navigate('/register')} className="flex-1">
                        Εγγραφή
                    </Button>
                </div>
            </form>
        </AuthLayout>
    );
}
